import { type FormDefinition, OTHER_CHOICE_ID } from "@fobu/schema";
import { computeFlow, type FlowIssue } from "./flow";
import { indexForm } from "./form-index";
import { type AnswerIssue, cleanAnswer, validateAnswer } from "./validate-answer";
import { renderTemplate } from "./template";

export interface ValidatedSubmission {
  /** Bereinigt: nur sichtbare Felder auf dem tatsächlichen Pfad. */
  answers: Record<string, unknown>;
  other: Record<string, string>;
  hidden: Record<string, string>;
  variables: Record<string, number | string>;
  endingId: string;
  /** Weiterleitungs-URL mit aufgelösten Platzhaltern (URL-kodiert). */
  redirectUrl?: string;
  pagePath: string[];
}

export type SubmissionResult =
  | { ok: true; data: ValidatedSubmission; warnings: FlowIssue[] }
  | { ok: false; errors: Record<string, AnswerIssue[]>; formErrors: string[]; firstInvalidPageId?: string };

/**
 * Prüfung einer bereits strukturell gültigen Einreichung (ohne zod – auch im Browser nutzbar).
 * Vertraut dem Client nicht: Pfad und Sichtbarkeit werden neu berechnet,
 * unbekannte/unsichtbare Antworten verworfen. Serverseitig `validateSubmission` verwenden.
 */
export function checkSubmission(def: FormDefinition, input: { answers: Record<string, unknown>; other?: Record<string, string>; hidden?: Record<string, string> }): SubmissionResult {
  const other0 = input.other ?? {};
  const idx = indexForm(def);

  // Zusammengesetzte Antworten (Adresse, Zeitraum) vorab bereinigen: ganz leer = nicht beantwortet.
  const answers0: Record<string, unknown> = { ...input.answers };
  for (const field of idx.fields) {
    if ((field.type !== "address" && field.type !== "date_range" && field.type !== "sepa") || answers0[field.id] === undefined) continue;
    const v = cleanAnswer(field, answers0[field.id]);
    if (v === undefined) delete answers0[field.id];
    else answers0[field.id] = v;
  }
  const flow = computeFlow(def, { answers: answers0, hidden: input.hidden });
  const errors: Record<string, AnswerIssue[]> = {};
  let firstInvalidPageId: string | undefined;

  for (const page of flow.pages) {
    for (const fieldId of page.visibleFieldIds) {
      const field = idx.fieldById.get(fieldId)!;
      const issues = validateAnswer(field, flow.answers[fieldId], other0[fieldId], { answers: flow.answers, hidden: flow.hidden, vars: flow.variables });
      if (issues.length) {
        errors[fieldId] = issues;
        firstInvalidPageId ??= page.pageId;
      }
    }
  }
  if (firstInvalidPageId) return { ok: false, errors, formErrors: [], firstInvalidPageId };

  // „Sonstiges“-Texte nur behalten, wenn die Option tatsächlich gewählt ist.
  const other: Record<string, string> = {};
  for (const [fieldId, text] of Object.entries(other0)) {
    const v = flow.answers[fieldId];
    const chosen = Array.isArray(v) ? v.includes(OTHER_CHOICE_ID) : v === OTHER_CHOICE_ID;
    if (chosen && text.trim()) other[fieldId] = text.trim();
  }

  // Gruppen bereinigen (unbekannte Unterfelder und leere Einträge entfernen).
  const answers: Record<string, unknown> = { ...flow.answers };
  for (const field of idx.fields) {
    if (field.type !== "group" || answers[field.id] === undefined) continue;
    const v = cleanAnswer(field, answers[field.id], { answers: flow.answers, hidden: flow.hidden, vars: flow.variables });
    if (v === undefined) delete answers[field.id];
    else answers[field.id] = v;
  }

  const ending = flow.ending;
  const redirectUrl = ending.redirectUrl
    ? renderTemplate(ending.redirectUrl, idx, { answers: flow.answers, other, vars: flow.variables, hidden: flow.hidden }, { encode: "url" })
    : undefined;

  return {
    ok: true,
    data: {
      answers,
      other,
      hidden: flow.hidden,
      variables: flow.variables,
      endingId: ending.id,
      ...(redirectUrl ? { redirectUrl } : {}),
      pagePath: flow.pages.map((p) => p.pageId),
    },
    warnings: flow.issues,
  };
}

/** Validierung einer einzelnen Seite (Renderer: „Weiter“-Button). */
export function validatePage(
  def: FormDefinition,
  pageId: string,
  input: { answers: Record<string, unknown>; other?: Record<string, string>; hidden?: Record<string, string> },
): Record<string, AnswerIssue[]> {
  const idx = indexForm(def);
  const flow = computeFlow(def, input);
  const page = flow.pages.find((p) => p.pageId === pageId);
  const errors: Record<string, AnswerIssue[]> = {};
  for (const fieldId of page?.visibleFieldIds ?? []) {
    const issues = validateAnswer(idx.fieldById.get(fieldId)!, flow.answers[fieldId], input.other?.[fieldId], { answers: flow.answers, hidden: flow.hidden, vars: flow.variables });
    if (issues.length) errors[fieldId] = issues;
  }
  return errors;
}

/** Kontingent-Ansprüche einer gültigen Einreichung; der Server prüft sie transaktional gegen die DB. */
export function capacityClaims(def: FormDefinition, answers: Record<string, unknown>): Array<{ fieldId: string; choiceId: string; capacity: number }> {
  const idx = indexForm(def);
  const claims: Array<{ fieldId: string; choiceId: string; capacity: number }> = [];
  for (const [fieldId, value] of Object.entries(answers)) {
    const field = idx.fieldById.get(fieldId);
    if (!field || !("choices" in field)) continue;
    const ids = Array.isArray(value) ? value.map(String) : [String(value)];
    for (const c of field.choices) if (c.capacity !== undefined && ids.includes(c.id)) claims.push({ fieldId, choiceId: c.id, capacity: c.capacity });
  }
  return claims;
}
